import { useCallback, useMemo, useSyncExternalStore } from "react";

export interface LeadFilters {
	from?: string;
	to?: string;
	employeeId?: string;
	formId?: string;
	zoneId?: string;
	outsideZone?: boolean;
	taskId?: string;
	flagged?: boolean;
}

/** Tartib BARQAROR — URL har renderda almashib turmasligi uchun. */
const TEXT_KEYS = ["from", "to", "employeeId", "formId", "zoneId", "taskId"] as const;
const BOOL_KEYS = ["outsideZone", "flagged"] as const;

export function parseLeadFilters(search: string): LeadFilters {
	const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
	const filters: LeadFilters = {};

	for (const key of TEXT_KEYS) {
		const value = params.get(key);

		if (value) {
			filters[key] = value;
		}
	}

	for (const key of BOOL_KEYS) {
		// ⚠️ FAQAT `"true"`. `"false"` — filtr yo'q degani, `false` EMAS:
		// aks holda serializatsiya → parslash aylanishi `{}` ni
		// `{ flagged: false }` ga aylantirardi va ro'yxat bilan xarita
		// turli so'rov yuborardi.
		if (params.get(key) === "true") {
			filters[key] = true;
		}
	}

	return filters;
}

export function serializeLeadFilters(filters: LeadFilters): string {
	const params = new URLSearchParams();

	for (const key of TEXT_KEYS) {
		const value = filters[key];

		if (value) {
			params.set(key, value);
		}
	}

	for (const key of BOOL_KEYS) {
		if (filters[key] === true) {
			params.set(key, "true");
		}
	}

	return params.toString();
}

/**
 * Ro'yxat va xarita filtrlarining YAGONA manbai (spec §8.4).
 *
 * ⚠️ **Nima uchun TanStack Router ning `useSearch()` i emas.**
 * `routeTree.tsx` — poydevor fayli va F2 unga tegmaydi (F2-N8), ya'ni
 * `/leads` va `/leads/map` marshrutlariga `validateSearch` qo'shib
 * bo'lmaydi. `validateSearch` siz `useSearch()` tiplanmagan `{}`
 * qaytaradi. History API to'g'ridan-to'g'ri ishlatiladi; sof
 * serializatsiya funksiyalari alohida test qilinadi.
 *
 * ⚠️ `replaceState` — `pushState` EMAS. Filtrni har o'zgartirish tarixga
 * yozilsa, «orqaga» tugmasi foydalanuvchini sahifadan chiqarish o'rniga
 * o'nlab filtr holatidan orqaga surardi.
 *
 * ⚠️ Maxsus hodisa MAJBURIY: `history.replaceState()` `popstate`
 * chiqarmaydi, ya'ni usiz komponentlar qayta render bo'lmasdi.
 */
const FILTER_EVENT = "skud:lead-filters";

function subscribe(onChange: () => void): () => void {
	window.addEventListener("popstate", onChange);
	window.addEventListener(FILTER_EVENT, onChange);

	return () => {
		window.removeEventListener("popstate", onChange);
		window.removeEventListener(FILTER_EVENT, onChange);
	};
}

export function useLeadFilters(): {
	filters: LeadFilters;
	setFilters: (next: LeadFilters) => void;
} {
	const search = useSyncExternalStore(
		subscribe,
		() => window.location.search,
		// SSR yo'q, lekin `useSyncExternalStore` server snapshot'ini talab
		// qiladi va uni tashlab ketish React ogohlantirishini beradi.
		() => ""
	);

	const filters = useMemo(() => parseLeadFilters(search), [search]);

	const setFilters = useCallback((next: LeadFilters) => {
		const qs = serializeLeadFilters(next);
		const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;

		window.history.replaceState(null, "", url);
		window.dispatchEvent(new Event(FILTER_EVENT));
	}, []);

	return { filters, setFilters };
}
